import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PluginIconPlaceholder } from '../components/common/PluginIconPlaceholder';
import { APP_CONFIG } from '../config/constants';

export default function CartPage() {
    const { user } = useAuth();
    const {
        cartItems,
        removeFromCart,
        getCartSubtotal,
        getCartTotal,
        applyCoupon,
        removeCoupon,
        coupon,
        discountAmount
    } = useCart();
    const navigate = useNavigate();

    const [couponCode, setCouponCode] = useState('');
    const [couponStatus, setCouponStatus] = useState({ loading: false, error: '' });

    const subtotal = getCartSubtotal();
    const total = getCartTotal();

    const handleApplyCoupon = async () => {
        if (!couponCode) return;
        setCouponStatus({ loading: true, error: '' });
        const { success, message } = await applyCoupon(couponCode);
        if (!success) {
            setCouponStatus({ loading: false, error: message });
        } else {
            setCouponStatus({ loading: false, error: '' });
            setCouponCode('');
        }
    };

    const handleRemoveCoupon = () => {
        removeCoupon();
        setCouponStatus({ loading: false, error: '' });
    };

    const handleCheckout = () => {
        navigate('/checkout');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 py-12">
            <div className="container mx-auto px-4">
                {/* Header com Gradiente */}
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-2">
                        <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-4xl font-extrabold text-gray-900">Seu Carrinho</h1>
                            <p className="text-gray-600">
                                {cartItems.length === 0 ? 'Nenhum item' : `${cartItems.length} ${cartItems.length === 1 ? 'item' : 'itens'}`}
                            </p>
                        </div>
                    </div>
                </div>

                {cartItems.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                        <div className="text-center py-20 px-6">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">Seu carrinho está vazio</h2>
                            <p className="text-gray-600 mb-8">Explore nossa coleção de plugins incríveis!</p>
                            <Link
                                to="/plugins"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-blue to-blue-600 text-white font-bold px-8 py-4 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                Ver Todos os Plugins
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Coluna de Itens do Carrinho */}
                        <div className="lg:col-span-2 space-y-4">
                            {cartItems.map((item, index) => (
                                <div
                                    key={item.id}
                                    className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 transform hover:-translate-y-1"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <div className="p-6">
                                        <div className="flex items-center gap-6">

                                            {/* Informações do Plugin */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-xl font-bold text-gray-900 mb-1 truncate group-hover:text-brand-blue transition-colors">
                                                    {item.name}
                                                </h3>
                                                <p className="text-sm text-gray-500">Plugin Premium</p>
                                            </div>

                                            {/* Preço e Ações */}
                                            <div className="text-right flex-shrink-0">
                                                <p className="text-2xl font-bold text-gray-900 mb-2">
                                                    {item.price === 0 ? (
                                                        <span className="text-green-600">Grátis</span>
                                                    ) : (
                                                        <>R$ {item.price.toFixed(2).replace('.', ',')}</>
                                                    )}
                                                </p>
                                                <button
                                                    onClick={() => removeFromCart(item.id)}
                                                    className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold text-sm hover:bg-red-50 px-4 py-2 rounded-lg transition-all"
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    Remover
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Coluna de Resumo do Pedido */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-6">
                                {/* Header do Resumo */}
                                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-200">
                                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                                        <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                        Resumo
                                    </h2>
                                </div>

                                <div className="p-6 space-y-6">
                                    {/* Cupom de Desconto */}
                                    <div className="space-y-3">
                                        <label className="flex items-center gap-2 font-semibold text-gray-700">
                                            <svg className="w-5 h-5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                            </svg>
                                            Cupom de Desconto
                                        </label>
                                        {coupon ? (
                                            <div className="flex justify-between items-center bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 p-4 rounded-xl">
                                                <div className="flex items-center gap-2">
                                                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span className="font-bold text-green-800">{coupon.code}</span>
                                                </div>
                                                <button
                                                    onClick={handleRemoveCoupon}
                                                    className="text-sm font-semibold text-green-700 hover:text-green-900 hover:underline"
                                                >
                                                    Remover
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={couponCode}
                                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                        placeholder="DIGITE O CÓDIGO"
                                                        className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-all font-mono"
                                                        disabled={couponStatus.loading}
                                                    />
                                                    <button
                                                        onClick={handleApplyCoupon}
                                                        disabled={couponStatus.loading || !couponCode}
                                                        className="bg-gradient-to-r from-brand-blue to-blue-600 text-white font-bold px-6 rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                                                    >
                                                        {couponStatus.loading ? (
                                                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        ) : 'Aplicar'}
                                                    </button>
                                                </div>
                                                {couponStatus.error && (
                                                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                        <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <p className="text-red-700 text-sm">{couponStatus.error}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Detalhes de Preço */}
                                    <div className="border-t-2 border-gray-100 pt-6 space-y-3">
                                        <div className="flex justify-between items-center text-gray-600">
                                            <span className="font-medium">Subtotal</span>
                                            <span className="font-semibold">R$ {subtotal.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                        {discountAmount > 0 && (
                                            <div className="flex justify-between items-center text-green-600">
                                                <span className="font-medium flex items-center gap-2">
                                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Desconto
                                                </span>
                                                <span className="font-bold">- R$ {discountAmount.toFixed(2).replace('.', ',')}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center text-2xl font-bold text-gray-900 pt-3 border-t-2 border-gray-100">
                                            <span>Total</span>
                                            <span className="text-brand-blue">R$ {total.toFixed(2).replace('.', ',')}</span>
                                        </div>
                                    </div>

                                    {/* Botões de Ação */}
                                    {user ? (
                                        <button
                                            onClick={handleCheckout}
                                            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-4 rounded-xl text-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Finalizar Compra
                                        </button>
                                    ) : (
                                        <div className="pt-4 space-y-3 border-t-2 border-gray-100">
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                                <div className="flex items-start gap-2">
                                                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-yellow-800 text-sm font-semibold">Faça login para continuar com a compra</p>
                                                </div>
                                            </div>
                                            <Link
                                                to="/login"
                                                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-brand-blue to-blue-600 text-white font-bold py-3 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
                                            >
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                                </svg>
                                                Fazer Login
                                            </Link>
                                            <Link
                                                to="/register"
                                                className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all"
                                            >
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                                </svg>
                                                Criar Conta
                                            </Link>
                                        </div>
                                    )}

                                    {/* Garantias e Segurança */}
                                    <div className="pt-4 border-t border-gray-100 space-y-2">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                            <span>Pagamento 100% seguro</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                            <span>Download imediato após compra</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                            <span>Suporte dedicado</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}